import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ResourcesLabels } from "../../../../util/Strings";
import { StandardFonts } from "pdf-lib";
import Select from "react-select";

import {
  addFile,
  resetMediaErrorAlert,
  setSelectedFile,
  fileUploadWithParams,
  fileDelete,
  removeEachFile,
} from "../../../../slices/FileUploadSlice";
import useFileValidation from "../../../../util/useFileValidation";
import ErrorMessageComponent from "../../../common-components/ErrorMessageComponent";
import Required from "../../../common-components/Required";
import FileUploader from "../../../common-components/FileUploader";
import {
  setPreThumbnailId,
  setPrevResourcesFileId,
  setPrevResourcesFileDetails,
  setPrevThumbnailDetails,
} from "../../../../slices/ResourcesSlice";
import ErrorAlert from "../../../common-components/ErrorAlert";
import ErrorMessages from "../../../../util/ErrorMessages";
import { ResourcesFormValidations } from "../../../../util/Validations";
import { useEffect } from "react";
import ImagePreviewWithFilename from "../../../common-components/ImagePreviewWithFilename";
import { grayscale, PDFDocument } from "pdf-lib";
import {
  DOCUMENTS,
  getSupportedFileExts,
  IMAGE,
  LINK,
  VIDEO,
  wrapText,
} from "../../../../util/Utils";

const AddResourcesForm = ({ lId, show, onClickNext, data }) => {
  const [state, setState] = useState({
    _id: data?._id,
    title: data?.title,
    description: data?.description,
    fileStorageId: data?.fileStorageId,
    fileType: data?.fileType,
    fileName: data?.fileName,
    fileThumbnailName: data?.fileThumbnailName,
    fileThumbnailId: data?.fileThumbnailId,
    fileThumbnailUrl: data?.fileThumbnailUrl,
    fileUrl: data?.fileUrl,
    isDeleteAllowed: data?.isDeleteAllowed,
    thumbNailId: data?.thumbNailId,
    fileId: data?.fileId,
    isValid: data?.isValid,
    isOnlineVideo: data?.isOnlineVideo,
  });

  const [validation, setValidation] = useState({});
  const [prevFileData, setPreFileData] = useState({
    fileUrl: data && data.fileUrl ? data.fileUrl : null,
    fileName: data && data.fileName ? data.fileName : "file",
  });

  const errorMessageCode = useSelector((state) => state.upload.errorCode);

  const [prevFileThumbnailData, setPrevFileThumbnailData] = useState({
    fileThumbnailUrl:
      data && data.fileThumbnailUrl ? data.fileThumbnailUrl : null,
    fileThumbnailName:
      data && data.fileThumbnailName ? data.fileThumbnailName : "file",
  });

  const checkValidation = () => {
    const { errors, isValid } = ResourcesFormValidations(state);
    setValidation(errors);
    return isValid;
  };

  const [prevFileId, setPrevFileId] = useState(
    data && data.photoId ? data.photoId : null
  );

  const [prevThumbanailFileId, setPrevThumbanailFileId] = useState(
    data && data.photoId ? data.photoId : null
  );

  const dispatch = useDispatch();

  const {
    errorSingleMessage,
    validateFile,
    validateResMaterialFiles,
    errorResMaterialMessage,
    setErrorResMaterialMessage,
    validateIsPdf,
  } = useFileValidation();

  useEffect(() => {
    onHandleNext();
  }, [state]);

  useEffect(() => {
    if (!state.fileType) {
      setErrorResMaterialMessage("Please select file type");
    } else {
      setErrorResMaterialMessage("");
    }
  }, [state.fileType]);

  function handleCloseErrorAlert() {
    dispatch(resetMediaErrorAlert(false));
    setPrevFileThumbnailData(prevFileThumbnailData);
    dispatch(setSelectedFile(null));
    setPreFileData(prevFileData);
    dispatch(setSelectedFile(null));
    handleFileState();
    handleFileThumbnailState();
  }

  const handleDrag = (event) => {
    event.preventDefault();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    postFileData(files);
  };

  const handleDropThumbnail = (event) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    postFileThumbnailData(files);
  };

  const handleFileSelect = (event) => {
    const files = event.target.files;
    postFileData(files);
  };

  const handleThumbnailFileSelect = (event) => {
    const files = event.target.files;
    postFileThumbnailData(files);
  };

  function handleFileState() {
    dispatch(fileDelete({ id: state?.fileId }));
    dispatch(removeEachFile({ id: lId, type: "file" }));
    setState({ ...state, ["fileId"]: "" });
  }

  function handleFileThumbnailState() {
    dispatch(fileDelete({ id: state?.thumbNailId }));
    dispatch(removeEachFile({ id: lId, type: "thumbnail" }));
    setState({ ...state, ["thumbnailId"]: "" });
  }

  if (errorMessageCode) {
    return (
      <ErrorAlert
        show={errorMessageCode}
        onClickHandle={handleCloseErrorAlert}
        title="Error"
        subTitle={ErrorMessages(errorMessageCode)}
      />
    );
  }

  function postFileThumbnailData(files) {
    const isValid = validateFile(files);
    if (isValid) {
      if (prevFileThumbnailData) {
        dispatch(setPreThumbnailId(prevFileThumbnailData));
        setPrevThumbanailFileId(prevThumbanailFileId);
        dispatch(setPrevThumbnailDetails(prevFileThumbnailData));
        setPrevFileThumbnailData({ photoUrl: null, fileName: null });
      }
      const file = files[0];
      if (file) {
        dispatch(addFile({ file, id: lId, type: "thumbnail" }));
        dispatch(fileUploadWithParams({ file, id: lId, type: "thumbnail" }))
          .unwrap()
          .then((res) => {
            setState({
              ...state,
              ["thumbNailId"]: res[0]._id,
              ["fileThumbnailName"]: res[0].fileName,
              ["fileThumbnailId"]: res[0].blobName,
            });
            setPrevThumbanailFileId(res[0]._id);
          });
      }
    }
  }

  async function postFileData(files) {
    let file = files[0];
    if (!state.fileType) {
      return;
    }
    if (validateIsPdf(file)) {
      file = await addDisclaimerToPDF(file);
    }
    const isValid = validateResMaterialFiles(files, state?.fileType);
    if (isValid) {
      if (prevFileData) {
        dispatch(setPrevResourcesFileId(prevFileId));
        setPrevFileId(prevFileData);
        dispatch(setPrevResourcesFileDetails(prevFileData));
        setPreFileData({ photoUrl: null, fileName: null });
      }
      if (file) {
        dispatch(addFile({ file, id: lId, type: "file" }));
        dispatch(fileUploadWithParams({ file, id: lId, type: "file" }))
          .unwrap()
          .then((res) => {
            setState({
              ...state,
              ["fileStorageId"]: res[0].blobName,
              ["fileName"]: res[0].fileName,
              ["fileId"]: res[0]._id,
            });
            setPrevFileId(res[0]._id);
          });
      }
    }
  }

  const onHandleNext = () => {
    if (checkValidation()) {
      if (state.title && state.description && state.fileType) {
        const newData = state;
        onClickNext({ ...newData, lId, isValid: true });
      }
    } else {
      const newData = state;
      onClickNext({ ...newData, lId, isValid: false });
    }
  };

  const fileTypeOptions = [
    { value: "video", label: "Video" },
    { value: "doc", label: "Documents" },
    { value: "link", label: "Link" },
    { value: "img", label: "Image" },
  ];

  const handleChange = (e) => {
    const value = e.target.value;
    setState({
      ...state,
      [e.target.name]: value,
    });
  };

  const addDisclaimerToPDF = async (file) => {
    const existingPdfBytes = await file.arrayBuffer();
    const pdfDoc = await PDFDocument.load(existingPdfBytes);
    const pages = pdfDoc.getPages();
    const fontSize = 12;
    const margin = 50;

    const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const { width, height } = pages[0].getSize();
    const maxWidth = width - margin * 2;
    const lineHeight = fontSize + 4;

    const page = pages[pages.length - 1];

    const lines = wrapText(
      ResourcesLabels.disclaimerText,
      maxWidth,
      fontSize,
      font
    );
    let y = 0;
    let currentPage = page
    lines.forEach((line) => {
      if (y < lineHeight) {
        currentPage = pdfDoc.addPage([width, height]);
        y = height - margin - lineHeight;
      }
      currentPage.drawText(line, {
        x: margin,
        y,
        size: fontSize,
        font,
        color: grayscale(0.5, 0.5, 0.5),
      });
      y -= lineHeight;
    });

    const pdfBytes = await pdfDoc.save();
    const newPdfBlob = new Blob([pdfBytes], { type: "application/pdf" });

    const newFile = new File([newPdfBlob], file.name, {
      type: file.type,
      lastModified: file.lastModified,
    });

    return newFile;
  };

  if (!show) {
    return null;
  }

  return (
    <>
      <div className="form-container">
        <div className="form-label-input">
          <label className="form-label">
            {ResourcesLabels.fileType}
            <Required />
          </label>
          <Select
            options={fileTypeOptions}
            className="basic-multi-select"
            isMulti={false}
            classNamePrefix="select"
            name="fileType"
            closeMenuOnSelect={true}
            defaultValue={
              data &&
              fileTypeOptions.filter((event) => event.label === data.fileType)
            }
            onChange={(e) => {
              setState({
                ...state,
                ["fileType"]: e.label,
                ["fileUrl"]: null,
                ["isOnlineVideo"]: false,
              });
            }}
          />
          <ErrorMessageComponent message={validation.fileType} />
        </div>

        {state.fileType && state?.fileType === VIDEO && (
          <div className="form-label-input-checkbox" style={{ marginTop: 0 }}>
            <div className="checkbox">
              <input
                className="checkbox-input"
                type="checkbox"
                defaultValue={state?.isOnlineVideo}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setState({ ...state, isOnlineVideo: checked });
                }}
              ></input>
            </div>
            <label className="form-label" style={{ marginBottom: 0 }}>
              {ResourcesLabels.videoLinkAvailable}
            </label>
          </div>
        )}

        <div className="form-label-input">
          <label className="form-label">
            {ResourcesLabels.title}
            <Required />
          </label>
          <div className="form-input-container">
            <input
              type="text"
              value={state.title}
              name="title"
              onChange={(e) => handleChange(e)}
              className="form-input"
              placeholder="Title"
              required={true}
            />
          </div>
          <ErrorMessageComponent message={validation.title} />
        </div>
        <div className="form-label-input">
          <label className="form-label">
            {ResourcesLabels.description}
            <Required />
          </label>
          <div className="user-form-input-container">
            <textarea
              type="text"
              value={state.description}
              name="description"
              multiple={true}
              rows={5}
              onChange={(e) => handleChange(e)}
              className="form-input"
              placeholder="Description"
            />
          </div>
          <ErrorMessageComponent message={validation.description} />
        </div>
        {((state?.fileType === VIDEO && state?.isOnlineVideo) ||
          state?.fileType === LINK) && (
          <div className="form-label-input">
            <label className="form-label">
              {state?.fileType === VIDEO
                ? ResourcesLabels.videoLink
                : ResourcesLabels.link}
              <Required />
            </label>
            <div className="form-input-container">
              <input
                type="url"
                value={state?.fileUrl}
                name="fileUrl"
                onChange={(e) => handleChange(e)}
                className="form-input"
                placeholder={
                  state?.fileType === VIDEO
                    ? ResourcesLabels.videoLink
                    : ResourcesLabels.link
                }
              />
            </div>
            <ErrorMessageComponent message={validation.fileUrl} />
          </div>
        )}
        {state.fileType !== LINK && (
          <FileUploader
            lId={lId}
            previewShape="rectangle"
            headerName={ResourcesLabels.fileThumbnailPhoto}
            handleDragOver={handleDrag}
            handleDrop={handleDropThumbnail}
            handleFileSelect={handleThumbnailFileSelect}
            handleState={handleFileThumbnailState}
            setPreFileData={setPrevFileThumbnailData}
            thumbnail={true}
            accept={getSupportedFileExts("Image")}
            type="resources"
          />
        )}
        {errorSingleMessage !== "" && (
          <p className="error image-error">{errorSingleMessage}</p>
        )}
        {state.fileType !== LINK && state?.fileThumbnailUrl && (
          <ImagePreviewWithFilename
            photoUrl={state?.fileThumbnailUrl}
            fileName={state?.fileThumbnailName}
            previewShape="rectangle"
            handleCancel={() => {
              dispatch(fileDelete({ id: state?.thumbNailId }))
                .unwrap()
                .then((res) => {
                  setState({
                    ...state,
                    fileThumbnailUrl: null,
                    fileThumbnailName: null,
                    thumbNailId: null,
                  });
                });
            }}
          />
        )}
        {!state.isOnlineVideo && state.fileType !== LINK && (
          <FileUploader
            lId={lId}
            previewShape="circular"
            headerName={ResourcesLabels.materials}
            handleDragOver={handleDrag}
            handleDrop={handleDrop}
            handleFileSelect={handleFileSelect}
            setPreFileData={setPreFileData}
            handleState={handleFileState}
            material={true}
            accept={getSupportedFileExts(state.fileType)}
            type="resources"
          />
        )}
        {errorResMaterialMessage !== "" && (
          <p className="error image-error">{errorResMaterialMessage}</p>
        )}
        {!state.isOnlineVideo && state.fileType !== LINK && state?.fileUrl && (
          <ImagePreviewWithFilename
            photoUrl={state?.fileUrl}
            fileName={state?.fileName}
            previewShape="rectangle"
            handleCancel={() => {
              dispatch(fileDelete({ id: state.fileId }))
                .unwrap()
                .then((res) => {
                  setState({
                    ...state,
                    fileId: null,
                    fileUrl: null,
                    fileStorageId: null,
                  });
                });
            }}
          />
        )}
        {((state.fileType === DOCUMENTS)|| (state.fileType === IMAGE)) && (
          <div className="form-label-input-checkbox" style={{ marginTop: 0 }}>
            <div className="checkbox">
              <input
                className="checkbox-input"
                type="checkbox"
                defaultValue={data?.isDeleteAllowed}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setState({ ...state, isDeleteAllowed: checked });
                }}
              ></input>
            </div>
            <label className="form-label">
              {ResourcesLabels.allowDownload}
            </label>
          </div>
        )}
      </div>
    </>
  );
};

export default AddResourcesForm;
